import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useSnackbar } from "igfd-commons/src/snackbar";

import { restaurantPlanUpdatedAction } from "@dashboard/store/actions/restaurantActions";
import type { ReduxState } from "@dashboard/types";

import {
  type UpdateRestaurantFeatureFlagVariables,
  useUpdateRestaurantFeatureFlagMutation,
} from "./queries";

export type UpdateFeatureFlag = (
  flag: UpdateRestaurantFeatureFlagVariables
) => Promise<void>;

// Posts one feature flag, writes the saved restaurant back to the store with
// its unchanged plan, and toasts on failure.
export const useFeatureFlagUpdater = (): UpdateFeatureFlag => {
  const dispatch = useDispatch();
  const snackbar = useSnackbar();
  const paymentPlansByRestaurantUuid = useSelector(
    (state: ReduxState) => state.restaurant.paymentPlansByRestaurantUuid
  );
  const { mutateAsync } = useUpdateRestaurantFeatureFlagMutation();

  return useCallback(
    (flag) =>
      mutateAsync(flag)
        .then((restaurant) => {
          dispatch(
            restaurantPlanUpdatedAction(
              restaurant.uuid,
              paymentPlansByRestaurantUuid[restaurant.uuid],
              restaurant
            )
          );
        })
        .catch(() => {
          snackbar.error("Couldn't save the change. Please try again.");
        }),
    [mutateAsync, dispatch, snackbar, paymentPlansByRestaurantUuid]
  );
};
